import { useState, useEffect } from "react";
import axios from "axios";
import Layout from "../components/Layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Skeleton } from "../components/ui/skeleton";
import {
  Building2,
  Plus,
  MapPin,
  DollarSign,
  Users,
  Edit,
  Trash2,
  UserPlus,
  UserMinus,
  Search,
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [employeeSearchOpen, setEmployeeSearchOpen] = useState(null);
  const [employeeEmail, setEmployeeEmail] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    unit_number: "",
    rent_amount: "",
    security_deposit: "",
    late_fee_amount: "50",
    late_fee_days: "5",
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/projects`);
      setProjects(response.data);
    } catch (error) {
      toast.error("Failed to load projects");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      rent_amount: parseFloat(formData.rent_amount),
      security_deposit: parseFloat(formData.security_deposit),
      late_fee_amount: parseFloat(formData.late_fee_amount),
      late_fee_days: parseInt(formData.late_fee_days),
    };

    try {
      if (editingProject) {
        await axios.put(`${API_URL}/api/projects/${editingProject.id}`, payload);
        toast.success("Project updated successfully");
      } else {
        await axios.post(`${API_URL}/api/projects`, payload);
        toast.success("Project created successfully");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save project");
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      address: project.address,
      unit_number: project.unit_number || "",
      rent_amount: project.rent_amount.toString(),
      security_deposit: project.security_deposit.toString(),
      late_fee_amount: project.late_fee_amount?.toString() || "50",
      late_fee_days: project.late_fee_days?.toString() || "5",
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (propertyId) => {
    try {
      await axios.delete(`${API_URL}/api/projects/${propertyId}`);
      toast.success("Project deleted successfully");
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete project");
    }
  };

  const resetForm = () => {
    setEditingProject(null);
    setFormData({
      name: "",
      address: "",
      unit_number: "",
      rent_amount: "",
      security_deposit: "",
      late_fee_amount: "50",
      late_fee_days: "5",
    });
  };

  const handleSearchEmployee = async (email) => {
    if (email.length < 3) {
      setSearchResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await axios.get(`${API_URL}/api/employees/search?email=${encodeURIComponent(email)}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAssignEmployee = async (propertyId, email) => {
    try {
      await axios.post(`${API_URL}/api/projects/${propertyId}/assign-employee?employee_email=${encodeURIComponent(email)}`);
      toast.success("Employee assigned successfully");
      setEmployeeSearchOpen(null);
      setEmployeeEmail("");
      setSearchResults([]);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign employee");
    }
  };

  const handleRemoveEmployee = async (propertyId) => {
    try {
      await axios.post(`${API_URL}/api/projects/${propertyId}/remove-employee`);
      toast.success("Employee removed successfully");
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove employee");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-64" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="projects-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Projects
            </h1>
            <p className="text-stone-600 mt-1">Manage your firm's projects and assigned employees.</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-900 hover:bg-emerald-800 rounded-full" data-testid="add-project-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Project
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {editingProject ? "Edit Project" : "Add New Project"}
                </DialogTitle>
                <DialogDescription>
                  {editingProject ? "Update project details below." : "Enter the details for your new project."}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Project Name</Label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="e.g., Sunset Apartments Unit 4B"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="project-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    name="address"
                    placeholder="123 Main St, City, State 12345"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    data-testid="project-address-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="unit_number">Unit Number (optional)</Label>
                  <Input
                    id="unit_number"
                    name="unit_number"
                    placeholder="e.g., 4B"
                    value={formData.unit_number}
                    onChange={handleChange}
                    data-testid="project-unit-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="rent_amount">Monthly Budget ($)</Label>
                    <Input
                      id="rent_amount"
                      name="rent_amount"
                      type="number"
                      step="0.01"
                      placeholder="1500"
                      value={formData.rent_amount}
                      onChange={handleChange}
                      required
                      data-testid="project-invoice-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="security_deposit">Retainer ($)</Label>
                    <Input
                      id="security_deposit"
                      name="security_deposit"
                      type="number"
                      step="0.01"
                      placeholder="1500"
                      value={formData.security_deposit}
                      onChange={handleChange}
                      required
                      data-testid="project-deposit-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="late_fee_amount">Late Fee ($)</Label>
                    <Input
                      id="late_fee_amount"
                      name="late_fee_amount"
                      type="number"
                      step="0.01"
                      placeholder="50"
                      value={formData.late_fee_amount}
                      onChange={handleChange}
                      data-testid="project-late-fee-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="late_fee_days">Grace Period (days)</Label>
                    <Input
                      id="late_fee_days"
                      name="late_fee_days"
                      type="number"
                      placeholder="5"
                      value={formData.late_fee_days}
                      onChange={handleChange}
                      data-testid="project-grace-days-input"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setIsDialogOpen(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-emerald-900 hover:bg-emerald-800" data-testid="project-submit-btn">
                    {editingProject ? "Update Project" : "Create Project"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Projects Grid */}
        {projects.length === 0 ? (
          <Card className="border-dashed" data-testid="no-projects">
            <CardContent className="py-12">
              <div className="text-center">
                <Building2 className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-stone-900 mb-2">No projects yet</h3>
                <p className="text-stone-600 mb-6 max-w-sm mx-auto">
                  Start by adding your first project to begin collecting invoice.
                </p>
                <Button
                  onClick={() => setIsDialogOpen(true)}
                  className="bg-emerald-900 hover:bg-emerald-800 rounded-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Project
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project) => (
              <Card key={project.id} className="card-hover" data-testid={`project-card-${project.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-emerald-700" />
                    </div>
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleEdit(project)}
                        data-testid={`edit-project-${project.id}`}
                      >
                        <Edit className="w-4 h-4 text-stone-500" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            data-testid={`delete-project-${project.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Project</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{project.name}"? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(project.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                  <CardTitle className="text-lg mt-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {project.name}
                  </CardTitle>
                  <CardDescription className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {project.address}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <p className="text-xs text-stone-500">Monthly Budget</p>
                      <p className="text-lg font-bold text-emerald-700">
                        {formatCurrency(project.rent_amount)}
                      </p>
                    </div>
                    <div className="p-3 bg-stone-50 rounded-lg">
                      <p className="text-xs text-stone-500">Deposit</p>
                      <p className="text-lg font-bold text-stone-900">
                        {formatCurrency(project.security_deposit)}
                      </p>
                    </div>
                  </div>

                  {/* Employee Section */}
                  <div className="pt-3 border-t border-stone-100">
                    {project.employee_name ? (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <Users className="w-4 h-4 text-blue-700" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-stone-900">{project.employee_name}</p>
                            <p className="text-xs text-stone-500">Employee</p>
                          </div>
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`remove-employee-${project.id}`}
                            >
                              <UserMinus className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Remove Employee</AlertDialogTitle>
                              <AlertDialogDescription>
                                Remove {project.employee_name} from {project.name}?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => handleRemoveEmployee(project.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Remove
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    ) : (
                      <Dialog
                        open={employeeSearchOpen === project.id}
                        onOpenChange={(open) => {
                          setEmployeeSearchOpen(open ? project.id : null);
                          if (!open) {
                            setEmployeeEmail("");
                            setSearchResults([]);
                          }
                        }}
                      >
                        <DialogTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full"
                            data-testid={`assign-employee-${project.id}`}
                          >
                            <UserPlus className="w-4 h-4 mr-2" />
                            Assign Employee
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Assign Employee</DialogTitle>
                            <DialogDescription>
                              Search for a registered employee by their email address.
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4">
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                              <Input
                                placeholder="Search employee email..."
                                value={employeeEmail}
                                onChange={(e) => {
                                  setEmployeeEmail(e.target.value);
                                  handleSearchEmployee(e.target.value);
                                }}
                                className="pl-10"
                                data-testid="employee-search-input"
                              />
                            </div>
                            {isSearching && <p className="text-sm text-stone-500">Searching...</p>}
                            {searchResults.length > 0 && (
                              <div className="space-y-2">
                                {searchResults.map((employee) => (
                                  <div
                                    key={employee.id}
                                    className="flex items-center justify-between p-3 bg-stone-50 rounded-lg hover:bg-stone-100 cursor-pointer"
                                    onClick={() => handleAssignEmployee(project.id, employee.email)}
                                  >
                                    <div>
                                      <p className="font-medium text-stone-900">{employee.full_name}</p>
                                      <p className="text-sm text-stone-500">{employee.email}</p>
                                    </div>
                                    <Button size="sm" variant="ghost">
                                      Assign
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                            {employeeEmail.length >= 3 && searchResults.length === 0 && !isSearching && (
                              <p className="text-sm text-stone-500 text-center py-4">
                                No employees found. Make sure they've registered as a employee.
                              </p>
                            )}
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Projects;
